import { useQueryCoaDocument } from '@/hooks/product-detail.hook';
import { Button, Input, Pagination } from '@/shared/components';
import { CoaAssets, ProductDetailQueryCoaReq } from '@/types/product-detail';
import dayjs from 'dayjs';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import type { UrlObject } from 'url';
import { SDSButton } from './sds-button';
import clsx from 'clsx';
import { TableColumn } from '@/shared/components/Table/use-column';
import { SpinnerBox } from '@/shared/pageComponents/spinner-box';
import ResponsiveTable from '@/shared/pageComponents/responsive-table';
import { useRouter } from 'next/navigation';
import Cookies from 'js-cookie';
import '../index.scss';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import { COA_ROOT_DOMAIN } from '@/app/utils/constant';
import { useEventTracking } from '@/shared/tracking/use-tracking';
import { pdpInteractions } from '@/shared/tracking/page-load';
interface IProps {
  className?: string;
  rootSku?: string;
  brandName?: string;
  childSku: string[];
  id?: string;
  childCatalogNumbers: string[];
}

type CoaTableProps = Pick<IProps, 'childSku' | 'rootSku' | 'brandName'>;

function DocumentDownload({ className, childSku, id, childCatalogNumbers, rootSku, brandName }: IProps) {
  const { t } = useLang(NAMESPACE.pdp);
  return (
    <div className={className} id={id}>
      <span className="text-[26px] leading-8 font-medium text-[#222]">{t('documents.downloads')}</span>
      <CoaTable childSku={childSku} rootSku={rootSku} brandName={brandName} />
      <div className="mt-12 mb-[70px]">
        <span className="text-[22px] leading-8 font-medium text-[#222]">{t('safety.data.sheets')}</span>
        <SDSButton className="mt-4" childCatalogNumbers={childCatalogNumbers} PDPPosition={'sds_down'} />
      </div>
    </div>
  );
}

function CoaTable({ childSku = [], rootSku, brandName }: CoaTableProps) {
  const { t } = useLang(NAMESPACE.pdp);
  const [lotSearch, setLogSearch] = useState('');
  const router = useRouter();
  const [queryCoaDocumentKey, setQueryCoaDocumentKey] = useState<ProductDetailQueryCoaReq>({
    lotNumber: '',
    sku: '',
    pageNo: 1,
    pageSize: 5,
    rootSku,
    brandName,
  });
  const { data, isLoading, isPreviousData } = useQueryCoaDocument(queryCoaDocumentKey);
  const [tableNoDataDes, setTableNoDataDes] = useState('');
  const { trackEvent } = useEventTracking();

  const tableLoading = useMemo(() => {
    if (!queryCoaDocumentKey.sku) return false;
    return isPreviousData || isLoading;
  }, [isLoading, isPreviousData, queryCoaDocumentKey.sku]);

  useEffect(() => {
    setQueryCoaDocumentKey({ ...queryCoaDocumentKey, sku: childSku.join(',') });
  }, [childSku]);

  useEffect(() => {
    if (!data) return;
    if (queryCoaDocumentKey && queryCoaDocumentKey.lotNumber !== '') {
      const totalResults = data.page?.count || 0;
      const searchStatus = data.code === '200' && totalResults ? 'search:successful' : 'search:failure';
      const searchObj = {
        searchType: 'COA',
        keyword: lotSearch,
        totalResults: totalResults,
      };
      trackEvent({
        key: 'search',
        action: searchStatus,
        payload: searchObj,
      });
    }
  }, [data?.data]);

  useEffect(() => {
    if (!data?.data || data?.data?.length === 0) {
      const emptyMessage = lotSearch === '' ? t('no.coa.available') : t('no.data.found');
      setTableNoDataDes(emptyMessage);
    }
  }, [data?.data]);

  const changeLotSearch = (e) => {
    setLogSearch(e.target.value);
  };

  const handleSearch = () => {
    setQueryCoaDocumentKey({ ...queryCoaDocumentKey, lotNumber: lotSearch?.trim(), pageNo: 1 });
  };

  const handlePageClick = (page) => {
    setQueryCoaDocumentKey({ ...queryCoaDocumentKey, pageNo: page });
  };

  const handleSearchEnterKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const columns: TableColumn<CoaAssets>[] = [
    {
      accessorKey: 'lotNumber',
      header: <div className="flex items-center justify-center md:justify-start sm:justify-start">{t('lot')}</div>,
      cell: (row) => {
        return (
          <div className="flex items-center">
            <span className="icon-24-document-mono mr-4 my-1" />
            <Link
              target="_blank"
              href={row.path?.startsWith('http') ? row.path : COA_ROOT_DOMAIN + row.path}
              onClick={() => {
                trackEvent({
                  key: 'eventsData',
                  action: 'documentDownloaded',
                  payload: {
                    event_name: 'documentDownloaded',
                    download_type: 'COA',
                    download_name: row.lotNumber,
                    download_link: row.path?.startsWith('http') ? row.path : COA_ROOT_DOMAIN + row.path,
                  },
                });
              }}
            >
              {row.lotNumber}
            </Link>
          </div>
        );
      },
    },
    {
      accessorKey: 'documentType',
      header: t('certificate.type'),
    },
    {
      accessorKey: 'publishDate',
      header: t('date'),
      cell: (row) => {
        return <span>{row.publishDate ? dayjs(row.publishDate).format('MMM-DD-YYYY') : ''}</span>;
      },
    },
    {
      accessorKey: 'sku',
      header: t('catalog.numbers'),
      cell: (row) => {
        return (
          <>
            {row.sku.map((item, index) => (
              <span key={item}>
                <span>{item}</span>
                {index !== row.sku.length - 1 && <span className="mr-1">,</span>}
              </span>
            ))}
          </>
        );
      },
    },
  ];

  const tableClsx = clsx({
    'certificates-table': true,
    'w-full mt-6': true,
    'mb-4': !data?.data || data?.data.length === 0,
  });
  const handleClick = (type) => {
    trackEvent({
      key: 'eventsData',
      action: 'PDPInteraction',
      payload: {
        event_name: 'PDPInteraction',
        PDP_interaction_type: pdpInteractions['request_a_certificate'],
      },
    });
    Cookies.set('request_type', 'coa');
    router.push('/request-form');
  };

  const getSearchMoreCoasHref = (): UrlObject => {
    const query: Record<string, string> = {
      display: 'coa',
    };
    if (queryCoaDocumentKey.lotNumber) {
      query.lotNumber = queryCoaDocumentKey.lotNumber;
    }
    return {
      pathname: '/document-search',
      query,
    };
  };
  return (
    <SpinnerBox isLoading={tableLoading}>
      <div className="mt-8">
        <span className="text-[22px] leading-8 font-medium text-[#222]">Certificates</span>
        <div className="flex sm:flex-col items-center mt-[22px]">
          <div className="relative flex-1 mr-6 sm:mr-0 sm:w-full">
            <Input
              className="w-full"
              placeholder={t('search.by.lot.number.or.partial.lot.number')}
              value={lotSearch}
              onChange={changeLotSearch}
              onKeyDown={handleSearchEnterKey}
            />
            <span onClick={handleSearch}>
              <i className="icon-24-search-mono absolute top-[20px] right-[20px] -translate-y-2/4 text-gray-500 cursor-pointer"></i>
            </span>
          </div>
          <div className="flex sm:w-full">
            <Button type="outline" className="w-36 sm:mt-4 sm:flex-1" onClick={handleSearch}>
              {t('search')}
            </Button>
          </div>
        </div>
        <ResponsiveTable
          columns={columns}
          dataSource={data?.data || []}
          className={tableClsx}
          noDataDes={tableNoDataDes}
        />
        <div className="mt-5 flex items-center gap-4 sm:flex-wrap">
          <Link prefetch href={getSearchMoreCoasHref()} target="_blank" className="flex">
            <Button type="link" className="p-0 text-left flex items-center h-auto leading-none">
              {t('search.more.coas')}
            </Button>
          </Link>
        </div>
        {(data?.page?.count ?? 0) > queryCoaDocumentKey.pageSize && (
          <Pagination
            pageSize={queryCoaDocumentKey.pageSize}
            total={data?.page?.count}
            onPageChange={handlePageClick}
            currentPage={queryCoaDocumentKey.pageNo}
          />
        )}
      </div>
    </SpinnerBox>
  );
}

export { DocumentDownload };
